/**
 * useLeavePermissions Hook
 *
 * Provides leave-specific permission utilities based on ABAC permissions.
 * Handles multi-scope permissions for leave requests (team, department, tenant).
 * Also considers active delegations — a delegate can approve and view team
 * requests even without explicit ABAC approve permissions (SAP HRBAS00).
 */

import { usePermissions, Permissions } from '@/features/auth/hooks/usePermissions';
import { useHasActiveDelegation } from '@/hooks/useDelegations';
import { useUser } from '@/stores';

export type LeaveScope = 'self' | 'my-team' | 'my-department' | 'all';

export function useLeavePermissions(options?: { enabled?: boolean }) {
  const user = useUser();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canManageApprovalDelegations = hasPermission(
    Permissions.HRMS_APPROVAL_DELEGATION_MANAGE_SELF,
  );
  const delegationQueryEnabled =
    (options?.enabled ?? true) && canManageApprovalDelegations && Boolean(user?.employeeId);

  // Check if there are active incoming delegations for LEAVE_REQUEST entity type
  // Entity-type scoping prevents cross-module false positives (SAP HRBAS00)
  const hasActiveDelegationsToMe = useHasActiveDelegation('LEAVE_REQUEST', {
    enabled: delegationQueryEnabled,
  });
  const hasActiveEncashmentDelegationsToMe = useHasActiveDelegation('LEAVE_ENCASHMENT', {
    enabled: delegationQueryEnabled,
  });

  // Can view requests beyond own
  const canViewTeamRequests =
    hasAnyPermission([
      Permissions.HRMS_LEAVE_REQUEST_READ_TENANT,
      Permissions.HRMS_LEAVE_REQUEST_READ_DEPARTMENT,
      Permissions.HRMS_LEAVE_REQUEST_READ_TEAM,
    ]) || hasActiveDelegationsToMe;

  // Can approve requests (via ABAC permission OR active delegation)
  const canApproveRequests =
    hasAnyPermission([
      Permissions.HRMS_LEAVE_REQUEST_APPROVE_TENANT,
      Permissions.HRMS_LEAVE_REQUEST_APPROVE_DEPARTMENT,
      Permissions.HRMS_LEAVE_REQUEST_APPROVE_TEAM,
    ]) || hasActiveDelegationsToMe;

  const canApproveEncashments =
    hasAnyPermission([
      Permissions.HRMS_LEAVE_ENCASHMENT_APPROVE_TENANT,
      Permissions.HRMS_LEAVE_ENCASHMENT_APPROVE_DEPARTMENT,
      Permissions.HRMS_LEAVE_ENCASHMENT_APPROVE_TEAM,
    ]) || hasActiveEncashmentDelegationsToMe;

  // Determine maximum read scope level
  const getMaxReadScope = (): LeaveScope => {
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_TENANT)) return 'all';
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_DEPARTMENT)) return 'my-department';
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_TEAM)) return 'my-team';
    if (hasActiveDelegationsToMe) return 'my-team';
    return 'self';
  };

  // Determine maximum approve scope level
  const getMaxApproveScope = (): LeaveScope => {
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_APPROVE_TENANT)) return 'all';
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_APPROVE_DEPARTMENT)) return 'my-department';
    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_APPROVE_TEAM)) return 'my-team';
    if (hasActiveDelegationsToMe) return 'my-team';
    return 'self';
  };

  // Get available scope options for dropdown
  // Delegates get 'my-team' even without explicit read:team permission
  const getAvailableScopeOptions = (): { value: LeaveScope; label: string }[] => {
    const options: { value: LeaveScope; label: string }[] = [];

    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_TEAM) || hasActiveDelegationsToMe) {
      options.push({ value: 'my-team', label: 'My Team' });
    }

    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_DEPARTMENT)) {
      options.push({ value: 'my-department', label: 'My Department' });
    }

    if (hasPermission(Permissions.HRMS_LEAVE_REQUEST_READ_TENANT)) {
      options.push({ value: 'all', label: 'All Employees' });
    }

    return options;
  };

  // Can maintain leave types (SAP Time Off configuration pattern)
  const canReadLeaveTypes = hasPermission(Permissions.HRMS_LEAVE_TYPE_READ);
  const canCreateLeaveTypes = hasPermission(Permissions.HRMS_LEAVE_TYPE_CREATE);
  const canUpdateLeaveTypes = hasPermission(Permissions.HRMS_LEAVE_TYPE_UPDATE);
  const canDeleteLeaveTypes = hasPermission(Permissions.HRMS_LEAVE_TYPE_DELETE);
  const canManageLeaveTypes = canCreateLeaveTypes || canUpdateLeaveTypes || canDeleteLeaveTypes;

  // Can adjust balances — SAP PT_BAL00 pattern: HR Admin only
  const canAdjustBalance = hasPermission(Permissions.HRMS_LEAVE_ADMIN_MANAGE);

  // Can view other employees' balances (SAP P_ORGIN scope for leave quota)
  const canViewTeamBalances = hasAnyPermission([
    Permissions.HRMS_LEAVE_BALANCE_READ_TENANT,
    Permissions.HRMS_LEAVE_BALANCE_READ_DEPARTMENT,
    Permissions.HRMS_LEAVE_BALANCE_READ_TEAM,
  ]);

  return {
    canViewTeamRequests,
    canApproveRequests,
    canApproveEncashments,
    canReadLeaveTypes,
    canCreateLeaveTypes,
    canUpdateLeaveTypes,
    canDeleteLeaveTypes,
    canManageLeaveTypes,
    canAdjustBalance,
    canViewTeamBalances,
    getMaxReadScope,
    getMaxApproveScope,
    getAvailableScopeOptions,
    hasActiveDelegationsToMe,
    hasActiveEncashmentDelegationsToMe,
  };
}
